import JSZip from "jszip";
import { jsPDF } from "jspdf";
import { generateJaybeeReport } from "./generateJaybeeReport";
import { buildVqfFormModel } from "./vqfFormLayout";
import { generateFormConfirmationPDF } from "./generateFormConfirmationPDF";
import { partyScreeningRows, partyScreeningLine } from "./shared/party-screening.js";
import { reconcileCaseState } from "./shared/case-state.js";
import { caseFeatures } from "./shared/case-features.js";
import { cleanModelText } from "./shared/case-note-text.js";

// One-click case-file export (Art. 63 VQF Regulations — electronic documents printable on
// request). Assembles the complete paper-equivalent dossier client-side into a single ZIP:
// compliance report, every uploaded client document, per-person Sumsub identity reports,
// digitised Form A/K confirmations, the case-note audit trail, and the screening evidence.
// Built in the browser so document volume is not constrained by serverless limits. The ZIP
// is a convenience copy — the authoritative record remains the system file (Art. 7 AMLA).

const REPORT_STATUSES = ["approved", "approved_with_flags", "rejected", "requires_review"];
const safe = (s) => String(s || "").replace(/[^\w.\- ]+/g, "").trim().slice(0, 80) || "file";

const simplePdf = (title, lines) => {
  const doc = new jsPDF({ unit: "mm", format: "a4" });
  const ml = 18; const pw = 210 - ml * 2; let y = 18;
  doc.setFont("helvetica", "bold"); doc.setFontSize(12); doc.setTextColor(30, 45, 90);
  doc.text(title, ml, y); y += 8;
  doc.setFont("helvetica", "normal"); doc.setFontSize(9); doc.setTextColor(31, 41, 55);
  lines.forEach((line) => {
    const wrapped = doc.splitTextToSize(String(line || " "), pw);
    wrapped.forEach((w) => {
      if (y > 282) { doc.addPage(); y = 18; }
      doc.text(w, ml, y); y += 4.6;
    });
    y += 1.6;
  });
  return doc.output("blob");
};

export async function exportCaseFile(ctx, onProgress = () => {}) {
  const { supabase, session, report, documents, verifications, caseNotes, sumsubFormRequests,
    walletScreenings, entitySanctions, additionalEntityScreenings, fieldLabel, valueLabel, firmName, features, formA } = ctx;
  const zip = new JSZip();
  const problems = [];
  // v1_747: reconciled case state, so the exported dossier agrees with the board.
  const cs = reconcileCaseState({
    report,
    collected_data: session.collected_data,
    origin: session.origin,
    documents,
    documentTracking: [],
    sumsubVerifications: Object.fromEntries((verifications || []).filter(v => v && v.person_name).map(v => [v.person_name, v])),
    aiReview: cleanModelText(session.ai_review),
    walletScreenings,
    mlroDecision: session.mlro_decision,
    decisionRecord: session.collected_data && session.collected_data.decisionRecord,
    // Form A in the exports (22/10/2026): an individual's Form A rows and state, as the board has them.
    ...(formA && typeof formA === 'object' ? { formRequests: formA.formRequests, individualFormA: formA.individualFormA } : {}),
  // v1_827: the firm's live flags (the dashboard's), not the portal's saved copy.
  }, { features: caseFeatures(features) });

  // ── 01 Compliance report (narrative-backed, firm-branded) ──────────────────
  onProgress("Generating compliance report…");
  try {
    // #120: the officer's sign in, so the server can check the caller.
    const { data: { session: reportAuth } = {} } = (supabase && supabase.auth) ? await supabase.auth.getSession() : { data: {} };
    const res = await fetch("/api/generate-report", {
      method: "POST", headers: { "Content-Type": "application/json", Authorization: "Bearer " + ((reportAuth && reportAuth.access_token) || "") },
      body: JSON.stringify({
        sessionData: { id: session.id, company_name: session.company_name, contact_name: session.contact_name, contact_email: session.contact_email, status: session.status, mlro_decision: session.mlro_decision, mlro_notes: session.mlro_notes, approved_at: session.approved_at, next_review_date: session.next_review_date, report },
        documents: (documents || []).map((d) => ({ document_name: d.document_name || d.file_name, document_type: d.document_type || d.doc_type, assessment: d.assessment })),
        caseNotes: (caseNotes || []).slice(0, 15).map((n) => ({ content: n.content, author: n.author, created_at: n.created_at })),
        verifications: (verifications || []).map((v) => ({ person_name: v.person_name, status: v.status, applicant_id: v.applicant_id })),
        walletScreenings: (walletScreenings || []).map((w) => ({ address: w.address, asset: w.asset, risk_level: w.risk_level, risk_score: w.risk_score, cluster_name: w.cluster_name })),
      }),
    });
    const data = await res.json();
    if (data.success && data.narrative) {
      // The firm's live flags, as the dashboard's copy of this report has them (it
      // was built with none, so a sanctions firm's entity screening was left out),
      // and the individual Form A line (form-a-exports): both values are passed.
      const { blobUrl } = generateJaybeeReport(session, data.narrative, documents || [], verifications || [], walletScreenings || [], null, firmName || "", features || null, formA || null);
      const blob = await (await fetch(blobUrl)).blob();
      zip.file("01 - Compliance Report.pdf", blob);
    } else { problems.push("Compliance report: " + (data.error || "narrative unavailable")); }
  } catch (e) { problems.push("Compliance report: " + e.message); }

  // ── 02 Client documents (uploaded only — placeholders are outstanding items) ─
  const uploaded = (documents || []).filter((d) => d.file_path && d.file_path !== "pending");
  for (let i = 0; i < uploaded.length; i++) {
    const d = uploaded[i];
    onProgress(`Fetching document ${i + 1} of ${uploaded.length}…`);
    try {
      const { data, error } = await supabase.storage.from("onboarding-documents").download(d.file_path);
      if (error) throw new Error(error.message);
      const ext = (d.file_path.split(".").pop() || "pdf").slice(0, 5);
      const cls = safe(d.document_type || d.doc_type || "Document");
      const nm = safe(d.document_name || "file");
      zip.file(`02 - Client Documents/${cls} - ${nm}${nm.toLowerCase().endsWith("." + ext.toLowerCase()) ? "" : "." + ext}`, data);
    } catch (e) { problems.push(`Document "${d.document_name}": ${e.message}`); }
  }
  const pendingDocs = (documents || []).filter((d) => d.file_path === "pending" && !d.file_url);
  if (pendingDocs.length) {
    zip.file("02 - Client Documents/OUTSTANDING.txt", "Documents recorded on the file whose uploaded copy was not retained (re-upload required):\n\n" + pendingDocs.map((d) => "- " + (d.document_type || d.document_name || "Document")).join("\n"));
  }

  // ── 03 Identity reports (per-person Sumsub PDFs) ───────────────────────────
  const reportable = (verifications || []).filter((v) => REPORT_STATUSES.includes(v.status));
  for (let i = 0; i < reportable.length; i++) {
    const v = reportable[i];
    onProgress(`Fetching identity report ${i + 1} of ${reportable.length} (${v.person_name})…`);
    try {
      let path = v.report_path;
      if (!path) {
        const r = await fetch("/api/sumsub-report", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ sessionId: v.session_id || session.id, applicantId: v.applicant_id, externalUserId: v.external_user_id }) });
        const d = await r.json();
        if (!d.success) throw new Error(d.error || "report unavailable");
        path = d.path;
      }
      const { data, error } = await supabase.storage.from("onboarding-documents").download(path);
      if (error) throw new Error(error.message);
      zip.file(`03 - Identity Reports/${safe(v.person_name)} - Identity Report.pdf`, data);
    } catch (e) { problems.push(`Identity report ${v.person_name}: ${e.message}`); }
  }

  // ── 04 Form A/K confirmations (digitised VQF forms) ────────────────────────
  const confirmed = (sumsubFormRequests || []).filter((r) => (r.form_type === "A" || r.form_type === "K") && r.status === "confirmed");
  for (let i = 0; i < confirmed.length; i++) {
    const rec = confirmed[i];
    onProgress(`Generating Form ${rec.form_type} confirmation ${i + 1} of ${confirmed.length}…`);
    try {
      // Hotfix 13/10/2026: the case id and the officer's sign in, so the server can check the caller.
      const params = new URLSearchParams({ action: "form-answers", applicantId: rec.applicant_id || "", sessionId: session?.id || "" });
      if (rec.applicant_action_id) params.set("applicantActionId", rec.applicant_action_id);
      const { data: { session: auth } = {} } = (supabase && supabase.auth) ? await supabase.auth.getSession() : { data: {} };
      const r = await fetch(`/api/sumsub?${params.toString()}`, { headers: { Authorization: "Bearer " + ((auth && auth.access_token) || "") } });
      const d = await r.json();
      const model = buildVqfFormModel({ record: rec, questionnaire: d.success && d.found ? d.questionnaire : { sections: {} }, session, report, fieldLabel, valueLabel });
      const blob = generateFormConfirmationPDF(model, { returnBlob: true, firmName });
      zip.file(`04 - Form Confirmations/Form ${rec.form_type} - ${safe(rec.signer_name)}.pdf`, blob);
    } catch (e) { problems.push(`Form ${rec.form_type} (${rec.signer_name}): ${e.message}`); }
  }

  // ── 05 Case notes (audit trail) ────────────────────────────────────────────
  onProgress("Compiling case notes…");
  const noteLines = (caseNotes || []).length
    ? (caseNotes || []).map((n) => `${n.created_at ? new Date(n.created_at).toLocaleString("en-GB") : ""} — ${n.author || ""}\n${n.content || ""}\n`)
    : ["No case notes on file."];
  zip.file("05 - Case Notes.pdf", simplePdf(`Case Notes — ${session.company_name || ""}`, noteLines));

  // ── 06 Screening evidence ──────────────────────────────────────────────────
  onProgress("Compiling screening evidence…");
  const scr = [];
  if (entitySanctions) {
    const d = entitySanctions.data || {};
    scr.push(`ENTITY SANCTIONS SCREENING — OpenSanctions`);
    scr.push(`Entity: ${d.entityChecked || session.company_name || ""} · Result: ${entitySanctions.status} · ${d.checkedAt ? new Date(d.checkedAt).toLocaleString("en-GB") : ""}`);
    if (d.listsSearched) scr.push(`Lists: ${d.listsSearched}`);
    const dism = d.dismissed || {};
    [...(d.hits || []), ...(d.cautionMatches || [])].forEach((h) => {
      scr.push(`Match: ${h.name} (${h.score}%) — ${(h.datasets || []).join(", ")}${dism[h.name] ? ` — FALSE POSITIVE: ${dism[h.name].reason} (${dism[h.name].dismissedBy}, ${new Date(dism[h.name].dismissedAt).toLocaleDateString("en-GB")})` : " — UNRESOLVED"}`);
    });
    scr.push("");
  } else { scr.push("ENTITY SANCTIONS SCREENING — not yet run", ""); }
  // Per-director/UBO OpenSanctions results (v1_645): one line per individual,
  // an independent OpenSanctions record alongside the entity above. This is not
  // the Sumsub identity report (section 03) — a separate, distinct control.
  const _osParties = partyScreeningRows(report, (session.collected_data || {}).partyScreenings, (session.collected_data || {}).screeningResolutions);
  if (_osParties.length) {
    scr.push("INDIVIDUAL PARTIES (OpenSanctions)");
    _osParties.forEach((r) => scr.push("  " + partyScreeningLine(r)));
    scr.push("");
  }
  (additionalEntityScreenings || []).forEach((a) => {
    scr.push(`ADDITIONAL ENTITY: ${a.name} · Result: ${a.status} · ${a.checkedAt ? new Date(a.checkedAt).toLocaleString("en-GB") : ""}`);
    const dism = (a.data && a.data.dismissed) || {};
    [...((a.data && a.data.hits) || []), ...((a.data && a.data.cautionMatches) || [])].forEach((h) => {
      scr.push(`  Match: ${h.name} (${h.score}%)${dism[h.name] ? ` — FALSE POSITIVE: ${dism[h.name].reason} (${dism[h.name].dismissedBy})` : " — UNRESOLVED"}`);
    });
  });
  if ((walletScreenings || []).length) {
    scr.push("", "WALLET SCREENINGS — Chainalysis");
    walletScreenings.forEach((w) => scr.push(`${w.address} (${w.asset}) — Risk: ${w.risk_level}, Score: ${w.risk_score}/10${w.cluster_name ? `, Cluster: ${w.cluster_name}` : ""}`));
  }
  zip.file("06 - Screening/Screening Evidence.pdf", simplePdf(`Screening Evidence — ${session.company_name || ""}`, scr));

  // ── 07 Officer resolutions and acceptances (v1_747) ────────────────────────
  // Each officer resolution and the source of wealth / funds acceptance, with the
  // officer, the date and the reason, and the model's own assessment beneath it;
  // then the reconciled ownership chain, one line per leg with its source.
  onProgress("Recording officer resolutions…");
  const ores = [];
  const _d = (at) => at ? new Date(at).toLocaleDateString("en-GB") : "";
  if (cs.sow.accepted || cs.sof.accepted) {
    ores.push("SOURCE OF WEALTH AND FUNDS");
    ores.push(`Accepted by the MLRO${cs.sof.by ? " (" + cs.sof.by + ")" : ""}${_d(cs.sof.at) ? " on " + _d(cs.sof.at) : ""}.`);
    if (cs.sof.note) ores.push(`Reason: ${cs.sof.note}`);
    ores.push(`The analysis assessed source of wealth: ${cs.sow.modelScore || "not scored"}; source of funds: ${cs.sof.modelScore || "not scored"}.`);
    ores.push("");
  }
  const _otherRes = cs.officerResolutions.filter(x => x.key !== "funds");
  if (_otherRes.length) {
    ores.push("OTHER OFFICER RESOLUTIONS");
    _otherRes.forEach(x => {
      ores.push(`${x.question}: Accepted by ${x.by || "the MLRO"}${_d(x.at) ? " on " + _d(x.at) : ""}.`);
      if (x.note) ores.push(`  Reason: ${x.note}`);
      if (x.modelAssessment) ores.push(`  The analysis assessed: ${x.modelAssessment}`);
    });
    ores.push("");
  }
  if (!ores.length) ores.push("No officer resolutions or acceptances recorded on this file.", "");
  ores.push("RECONCILED OWNERSHIP CHAIN");
  if ((cs.ownership.ownershipChain || []).length) {
    cs.ownership.ownershipChain.forEach(l => ores.push(`${l.holder} holds ${l.percentage || "an unstated share"} of ${l.held}${l.source ? " (source: " + l.source + ")" : ""}`));
  } else {
    ores.push("No ownership chain legs recorded (direct ownership, or not yet mapped).");
  }
  zip.file("07 - Officer Resolutions and Acceptances.pdf", simplePdf(`Officer Resolutions and Acceptances — ${session.company_name || ""}`, ores));

  if (problems.length) zip.file("EXPORT-NOTES.txt", "Items that could not be included:\n\n" + problems.join("\n"));

  onProgress("Building ZIP…");
  const blob = await zip.generateAsync({ type: "blob" });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = `CaseFile-${safe(session.company_name || "Client").replace(/\s+/g, "-")}-${new Date().toISOString().slice(0, 10)}.zip`;
  a.click();
  setTimeout(() => URL.revokeObjectURL(a.href), 10000);
  onProgress("");
  return { problems };
}
